import { parseDateStringToLocal } from './formatters';

// ════════════════════════════════════════════════════════════════════════════════
// Fechas de vencimiento: descartar las que no son fechas.
//
// En producción hay documentos cuya `fecha_vencimiento` quedó grabada con un año
// imposible — el caso que lo destapó fue un "Reglamento Interno" con 0299-12-30, que
// el panel de Pre-SISI mostraba como «Vencido — Hace 630654 d». No es un documento
// vencido hace 1.726 años: es un dato corrupto de alguna carga masiva.
//
// Un año fuera de [1900, 2200] no puede ser una fecha de vencimiento real de este
// sistema, así que se trata como "sin fecha" (no como vencido) en todos lados: el
// documento deja de inventar un conteo de días y de inflar el contador de vencidos, y
// aparece en Estado de Documentación bajo "Sin Fecha" con la etiqueta «Fecha inválida»
// para que alguien pueda corregir el registro.
//
// El rango es deliberadamente ancho: acá se decide "¿esto es una fecha?", no "¿es
// razonable?". Una fecha antigua pero legítima (un documento vencido en 2015) tiene
// que seguir contando como vencida.
// ════════════════════════════════════════════════════════════════════════════════

export const ANIO_MIN_PLAUSIBLE = 1900;
export const ANIO_MAX_PLAUSIBLE = 2200;

/** ¿La fecha existe y cae en un año creíble? Un Date inválido también da false. */
export const esFechaPlausible = (d?: Date | null): boolean => {
  if (!d || Number.isNaN(d.getTime())) return false;
  const anio = d.getFullYear();
  return anio >= ANIO_MIN_PLAUSIBLE && anio <= ANIO_MAX_PLAUSIBLE;
};

/**
 * Parsea una fecha del backend en cualquiera de los formatos que llegan
 * (dd/mm/yyyy, yyyy/mm/dd, ISO, date-only) y devuelve null si no parsea o si el año
 * está fuera de rango. Único punto donde se decide qué es una fecha usable.
 */
export const parseFechaVencimiento = (value?: string | null): Date | null => {
  if (!value || typeof value !== 'string') return null;
  const s = value.trim();
  if (!s) return null;

  let d: Date | null = null;
  // Año tal como viene escrito en el string. Hace falta compararlo contra el año del Date
  // resultante porque `new Date(y, m, d)` remapea los años 0..99 al 1900..1999: '0001-01-01'
  // se convertiría en 1901 y pasaría por una fecha antigua legítima en vez de por basura.
  let anioDeclarado: number | null = null;

  // dd/mm/yyyy
  const m1 = s.match(/^([0-3]?\d)\/(0?\d|1[0-2])\/(\d{4})$/);
  if (m1) {
    const [, dd, mm, yyyy] = m1;
    anioDeclarado = Number(yyyy);
    d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  }
  // yyyy/mm/dd
  if (!d) {
    const m2 = s.match(/^(\d{4})\/(0?\d|1[0-2])\/([0-3]?\d)$/);
    if (m2) {
      const [, yyyy, mm, dd] = m2;
      anioDeclarado = Number(yyyy);
      d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    }
  }
  // ISO / date-only: helper local para no correr un día por huso horario
  if (!d) {
    const iso = s.match(/^(\d{4})-/);
    if (iso) anioDeclarado = Number(iso[1]);
    d = parseDateStringToLocal(s);
  }

  if (!esFechaPlausible(d)) return null;
  if (anioDeclarado !== null && d.getFullYear() !== anioDeclarado) return null;
  return d;
};

/**
 * Días entre hoy y la fecha (negativo = ya venció). null si la fecha no es usable, para
 * que quien lo muestre pinte «—» en vez de un número inventado.
 */
export const diasHastaVencimiento = (value?: string | null): number | null => {
  const d = parseFechaVencimiento(value);
  if (!d) return null;
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const venc = new Date(d.getTime());
  venc.setHours(0, 0, 0, 0);
  return Math.round((venc.getTime() - hoy.getTime()) / 86400000);
};

/**
 * ¿El registro trae una fecha de vencimiento escrita pero inutilizable? Distingue
 * «Fecha inválida» (hay que corregir el dato) de «Sin fecha» (documento sin vigencia).
 */
export const tieneFechaVencimientoInvalida = (value?: string | null): boolean =>
  Boolean(value && String(value).trim() && !parseFechaVencimiento(value));

/**
 * Tope para un conteo de días que viene ya calculado desde el backend (el `dias_restantes`
 * que el barrido de vencimientos guarda en la notificación). No se puede reparsear la fecha
 * desde ahí, así que se descarta por magnitud: cien años de atraso o de vigencia no es un
 * documento, es la misma fecha corrupta llegando por otra puerta.
 */
export const DIAS_MAX_CREIBLES = 36500;

export const esConteoDiasCreible = (dias?: number | null): boolean =>
  typeof dias === 'number' && Number.isFinite(dias) && Math.abs(dias) <= DIAS_MAX_CREIBLES;
